/**
 * Copyright 2022-2025 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useState } from "react";
import { RowSelectionState } from "@tanstack/table-core";
import { Else, If, Then } from "react-if";
import { Box, FlexContainer, Spacer, theme } from "@nordcloud/gnui";
import {
  MappingRule,
  MappingRulesSortFields,
  SortOrder,
} from "~/generated/graphql";
import { LoaderWrap } from "~/components/Loader";
import { NoData } from "~/components/NoData";
import { Pagination } from "~/components/Pagination";
import {
  minSearchLength,
  TableSearchInput,
  getNoSearchResultsMessage,
} from "~/components/SearchInput";
import { SortSelector } from "~/components/SortSelector";
import { TableWrapper } from "~/components/Tables";
import { useQueryState } from "~/hooks";
import { isEmpty, isNotEmpty, isNotNil } from "~/tools";
import { useGetMappingRules } from "../hooks/useGetMappingRules/useGetMappingRule";
import { MappingRuleQuery } from "../types";
import { columns } from "./MappingRulesTableColumns";

const sortOptions = [
  {
    value: MappingRulesSortFields.Type,
    label: "Type",
  },
];

export function MappingRulesTable() {
  const { queryState, updateQueryState } = useQueryState<MappingRuleQuery>();
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const handleSortDirection = (order: SortOrder) => {
    updateQueryState({
      ...queryState,
      order,
    });
  };

  const handleSortField = (field: MappingRulesSortFields) => {
    updateQueryState({
      ...queryState,
      field,
    });
  };

  const handlePhraseField = (field: string | undefined) => {
    updateQueryState({
      ...queryState,
      searchPhrase: field,
    });
  };

  const isSearchAllowed =
    !queryState.searchPhrase ||
    queryState.searchPhrase.length >= minSearchLength;

  const { data, loading, count } = useGetMappingRules({
    sort: {
      field: queryState.field ?? MappingRulesSortFields.Type,
      order: queryState.order ?? SortOrder.Asc,
    },
    filter: {
      phrase: isNotEmpty(queryState.searchPhrase?.trim() || "")
        ? queryState.searchPhrase
        : undefined,
    },
  });

  return (
    <>
      <Box boxStyle="lightGrey" spacing="spacing04">
        <FlexContainer justifyContent="space-between">
          <SortSelector<MappingRulesSortFields>
            options={sortOptions}
            value={queryState.field ?? MappingRulesSortFields.Type}
            direction={queryState.order ?? SortOrder.Asc}
            onSelect={handleSortField}
            onSortChange={handleSortDirection}
          />
          <Spacer width={theme.spacing.spacing04} />
          <TableSearchInput<MappingRuleQuery>
            onSubmit={handlePhraseField}
            placeholder="Type a Keyword (e.g. Tag Key)"
          />
        </FlexContainer>
      </Box>
      <If condition={!isSearchAllowed}>
        <Then>
          <NoData hasIcon message={getNoSearchResultsMessage()} />
        </Then>
        <Else>
          <LoaderWrap loading={loading} inContent>
            <If condition={isEmpty(data)}>
              <Then>
                <NoData hasIcon message={"There are no Mapping Rules."} />
              </Then>
              <Else>
                <>
                  <TableWrapper<MappingRule>
                    loading={loading}
                    data={data.filter(isNotNil)}
                    columns={columns()}
                    rowSelection={rowSelection}
                    setRowSelection={setRowSelection}
                    stripped
                  />
                  <Pagination count={count} />
                </>
              </Else>
            </If>
          </LoaderWrap>
        </Else>
      </If>
    </>
  );
}
